import { useState } from "react";

import { DEFAULT_FONT_FAMILY, DEFAULT_FONT_SIZE } from "@excalidraw/common";
import { newTextElement } from "@excalidraw/element";

import { useUIAppState } from "../../context/ui-appState";
import { useApp, useExcalidrawSetAppState } from "../App";
import { Dialog } from "../Dialog";
import { ArrowRightIcon } from "../icons";
import { withInternalFallback } from "../hoc/withInternalFallback";
import Spinner from "../Spinner";

import "./AIGenerateDialog.scss";

export type OnAITextSubmit = (prompt: string) => Promise<{
  text?: string | null;
  error?: Error | null;
}>;

export const AITextDialog = (
  props: { onSubmit: OnAITextSubmit } | { __fallback: true },
) => {
  const appState = useUIAppState();

  if (appState.openDialog?.name !== "ai-text") {
    return null;
  }

  return <AITextDialogContent {...props} />;
};

const AITextDialogContent = withInternalFallback(
  "AITextDialogContent",
  (props: { onSubmit: OnAITextSubmit } | { __fallback: true }) => {
    const app = useApp();
    const setAppState = useExcalidrawSetAppState();

    const [prompt, setPrompt] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [generatedText, setGeneratedText] = useState<string | null>(null);

    const handleGenerate = async () => {
      if ("__fallback" in props || prompt.trim().length < 3 || loading) {
        return;
      }

      setLoading(true);
      setError(null);
      setGeneratedText(null);

      try {
        const result = await props.onSubmit(prompt.trim());
        if (result.error) {
          setError(result.error.message);
        } else if (result.text) {
          setGeneratedText(result.text);
        } else {
          setError("Nie udało się wygenerować tekstu");
        }
      } catch (err: any) {
        setError(err?.message || "Błąd generowania");
      } finally {
        setLoading(false);
      }
    };

    const handleInsert = () => {
      if (!generatedText) {
        return;
      }

      const element = newTextElement({
        x: 100,
        y: 100,
        text: generatedText,
        fontSize: DEFAULT_FONT_SIZE,
        fontFamily: DEFAULT_FONT_FAMILY,
        textAlign: "left",
        verticalAlign: "top",
      });

      app.addElementsFromPasteOrLibrary({
        elements: [element],
        files: null,
        position: "center",
        fitToContent: true,
      });

      setAppState({ openDialog: null });
    };

    const isFallback = "__fallback" in props;

    return (
      <Dialog
        className="ai-generate-dialog"
        onCloseRequest={() => setAppState({ openDialog: null })}
        size={600}
        title="Generuj tekst AI"
      >
        <div className="ai-generate-dialog__content">
          {isFallback ? (
            <div className="ai-generate-dialog__fallback">
              Generowanie tekstu AI nie jest dostępne w tej wersji.
            </div>
          ) : (
            <>
              <div className="ai-generate-dialog__input-section">
                <label className="ai-generate-dialog__label">
                  Opisz tekst, który chcesz wygenerować:
                </label>
                <textarea
                  className="ai-generate-dialog__textarea"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="np. Wyjaśnij fotosyntezę dla uczniów klasy 5"
                  rows={3}
                />
                <button
                  className="ai-generate-dialog__button ai-generate-dialog__button--primary"
                  onClick={handleGenerate}
                  disabled={prompt.trim().length < 3 || loading}
                >
                  {loading ? <Spinner /> : <>{ArrowRightIcon} Generuj</>}
                </button>
              </div>

              {error && (
                <div className="ai-generate-dialog__error">{error}</div>
              )}

              {generatedText && (
                <div className="ai-generate-dialog__preview">
                  <div className="ai-generate-dialog__preview-text">
                    {generatedText}
                  </div>
                  <button
                    className="ai-generate-dialog__button ai-generate-dialog__button--success"
                    onClick={handleInsert}
                  >
                    Wstaw na płótno
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Dialog>
    );
  },
);

